import axios, { AxiosError, AxiosResponse } from 'axios';
import { stringify } from 'qs';

declare const ajax_link: string;
declare const ajax_nonce: string;

const ajaxLink = ajax_link;
const ajaxNonce = ajax_nonce;

export interface PostRequest {
  controller: string;
  method: string;
  data?: {
    [key: string]: any;
  };
}

export interface LyceoApiRequest {
  action: 'lyceo';
  nonce: string;
  controller: string;
  method: string;
  [key: string]: any;
}

// Create a cancel token to prevent duplicate calls.
const CancelToken = axios.CancelToken;
const source = CancelToken.source();

/**
 * Post to the wordpress backend.
 * ajaxLink = (/wp-admin/admin-ajax.php)
 *
 * The action "lyceo" will trigger Lyceo_Loader.ajax_controller();
 * In that method the ajaxNonce will be validated.
 *
 * When the nonce is invalid the user will receive the error:
 * "No naughty business please"
 *
 * After that the controller will be searched in the Controllers/Ajax directory.
 * When found the provided method in the Controller will be called.
 *
 * @throws AxiosError
 */
export async function post<T>(data: PostRequest): Promise<T> {
  let request: LyceoApiRequest = {
    action: 'lyceo',
    nonce: ajaxNonce,
    controller: data.controller,
    method: data.method,
  };

  // if additional data has been provided append this to the request.
  if (data.data) {
    request = { ...request, ...data.data };
  }

  // By default, axios serializes JavaScript objects to JSON.
  // The backend wants the data application/x-www-form-urlencoded.
  const urlEncodedContent = stringify(request);

  const response: AxiosResponse<T> = await axios.post<T>(
    ajaxLink,
    urlEncodedContent,
    {
      method: 'POST',
      headers: {
        'cache-control': 'no-cache',
        'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest',
      },
      // prevent duplicate calls.
      cancelToken: source.token,
    });

  // only return the response data.
  return response.data;
}

/**
 * Wrapper for axios get method.
 */
export async function get<T>(url: string): Promise<T> {
  const response: AxiosResponse<T> = await axios.get<T>(url);
  return response.data;
}
